import { DatePipe } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, Injector, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, NgControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';

export interface NxEditCalendarInputValue {
    precision: "DATE_TIME" | "DATE" | undefined;
    dateTime?: string | undefined;
    fromDateTime?: string | undefined;
    toDateTime?: string | undefined;
}

@Component({
    selector: 'nx-edit-calendar-input',
    template: `
    <ng-container [formGroup]="calendarFormGroup">
        <div class="nx-edit-calendar-form-control">
            <div class="flex flex-col form nx-edit-dateTime">
                <div>
                    <p-calendar
                        class="w-full p-datepicker-inline"
                        formControlName="calendar"
                        [style]="{'width':'270px'}"
                        [selectionMode]="selectionMode" 
                        id="fromDateTime"
                        dateFormat="dd.mm.yy"
                        [inline]="true">
                    </p-calendar>
                </div>
                <div
                    *ngIf="showTime"
                    class="flex flex-row basis-full gap-2">
                    <div class="basis-1/2 flex flex-col justify-start">
                        <label
                            *ngIf="getLeftTimeFormControlLabel()">
                            {{getLeftTimeFormControlLabel()}}</label>
                        <p-dropdown
                            #leftTimeDropdown
                            [panelStyle]="{'width': 'auto'}"
                            formControlName="leftTimeFormControl"
                            (onShow)="onOverlayOpen($event)"
                            (onHide)="onOverlayClose($event)"
                            (onClear)="$event.stopPropagation()"
                            [showClear]="true" 
                            [appendTo]="'body'"
                            [editable]="true" 
                            [options]="timeOptions"
                            (onBlur)="onTimeBlur($event, 'leftTimeFormControl')"
                            [virtualScroll]="false"
                            [filter]="false"
                            [placeholder]="'hh:mm'"
                            (onFocus)="onTimeFocus($event); leftTimeDropdown.show()"
                            class="leftTimeFormControl"
                            [panelStyleClass]="'nx-edit-select'"/>
                    </div>
                    <div class="basis-1/2 flex flex-col justify-start">
                        <ng-container *ngIf="isRightTimeFormControlRequired()">  
                            
                            <label
                                *ngIf="isRightTimeFormControlRequired() && getLeftTimeFormControlLabel()">
                                    {{getRightTimeFormControlLabel()}}</label>
                                <p-dropdown
                                    #rightTimeDropdown
                                    [panelStyle]="{'width': 'auto'}"
                                    formControlName="rightTimeFormControl"
                                    (onShow)="onOverlayOpen($event)"
                                    (onHide)="onOverlayClose($event)"
                                    (onClear)="$event.stopPropagation()"
                                    [showClear]="true" 
                                    [appendTo]="'body'"
                                    [editable]="true" 
                                    [options]="timeOptions"
                                    (onBlur)="onTimeBlur($event, 'rightTimeFormControl')"
                                    [virtualScroll]="false"
                                    [filter]="false"
                                    [placeholder]="'hh:mm'"
                                    (onFocus)="onTimeFocus($event); rightTimeDropdown.show()"
                                    class="rightTimeFormControl"
                                    [panelStyleClass]="'nx-edit-select'"/>
                        </ng-container>
                    </div>
                </div>
                    <label
                        *ngIf="showTimesError"
                        class="mt-[4px] ml-[2px] text-red-500">
                        Startzeit muss vor Endzeit liegen.</label>
            </div>
        </div>
    </ng-container>
  `,
    styles: [`
        .form > *:not(:last-child) {
            margin:0 !important;
        }
        :host ::ng-deep .p-dropdown .p-dropdown-trigger {
            display: none;
        }
        :host ::ng-deep .p-dropdown .p-icon-wrapper {
            position: absolute;
            right: 20px;
            top: 13px;
            transform: rotate(45deg);
        }
  `],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => EditCalendarInputComponent),
            multi: true
        }
    ]
})
export class EditCalendarInputComponent implements ControlValueAccessor, OnInit, OnDestroy, OnChanges, AfterViewInit {

    @Output() blockOverlayDismiss: EventEmitter<any> = new EventEmitter();
    @Output() unblockOverlayDismiss: EventEmitter<any> = new EventEmitter();

    ngControl: NgControl;
    isDisabled: boolean;
    showTimesError: boolean = false;
    blockNextChangeCycle: boolean = false;

        // Keep track of the event listeners we've added
        private navElementClickHandlers: { element: HTMLElement, handler: (event: Event) => void }[] = [];
        private mutationObserver: MutationObserver | null = null;

    @Input() selectionMode: 'single' | 'range' = 'single';
    @Input() showTime: boolean = false;

    private onChange: (value: any) => void;
    private onTouched: () => void;

    // the actual form, that gets the real values
    calendarFormGroup: FormGroup;
    calendarFormGroupSubscription: Subscription;
    calendarFormControl: FormControl;
    leftTimeFormControl: FormControl;
    rightTimeFormControl: FormControl;

    timeOptions: string[] = this.generateTimeOptions();

    constructor(
        private _injector: Injector,
        private datePipe: DatePipe,
        private el: ElementRef) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if(!this.blockNextChangeCycle) {
            if(!this.showTime) {
                this.leftTimeFormControl?.reset();
                this.rightTimeFormControl?.reset();
            }
            if(changes.selectionMode 
                && changes.selectionMode.currentValue === 'range' && changes.selectionMode.previousValue === 'single') {
                this.rightTimeFormControl?.reset();
            }
            if(changes.selectionMode 
                && changes.selectionMode.currentValue === 'single' && changes.selectionMode.previousValue === 'range') {
                this.calendarFormControl?.reset();
            }
        }
        this.blockNextChangeCycle = false;

        if (this.el && this.el.nativeElement) {
            setTimeout(() => {
                this.attachEventHandlersToCalendarNavigation();
            });
        }
    }

    ngOnInit() {
        this.ngControl = this._injector.get(NgControl);
        this.ngControl.valueAccessor = this;
        this.calendarFormGroup = new FormGroup({});
        this.calendarFormControl = new FormControl(undefined, [Validators.required]);
        this.calendarFormGroup.addControl('calendar', this.calendarFormControl);
        this.leftTimeFormControl = new FormControl(undefined, []);
        this.calendarFormGroup.addControl('leftTimeFormControl', this.leftTimeFormControl);
        this.rightTimeFormControl = new FormControl(undefined, []);
        this.calendarFormGroup.addControl('rightTimeFormControl', this.rightTimeFormControl);
        this.calendarFormGroupSubscription = this.calendarFormGroup.valueChanges.subscribe(value => {
            // check validity, build the value and call the onChange function
            this.onInputChange(value);
        });
    }

    ngAfterViewInit() {
        // Set up the mutation observer to watch for DOM changes
        this.setupMutationObserver();
        
        // Initially attach handlers
        this.attachEventHandlersToCalendarNavigation();
      }

      private setupMutationObserver() {
        // Create a mutation observer to detect changes to the calendar's DOM
        this.mutationObserver = new MutationObserver(() => {
            // When DOM changes, reattach our event handlers
            this.attachEventHandlersToCalendarNavigation();
        });

        // Start observing the component's DOM for structural changes
        if (this.el && this.el.nativeElement) {
            this.mutationObserver.observe(this.el.nativeElement, {
                childList: true,
                subtree: true
            });
        }
    }

    private attachEventHandlersToCalendarNavigation() {
        // First, remove any existing handlers to prevent duplicates
        this.removeExistingEventHandlers();

        // Find all calendar navigation elements
        const element = this.el.nativeElement;
        const navElements = element.querySelectorAll(
            '.p-datepicker-header .p-datepicker-prev, ' +
            '.p-datepicker-header .p-datepicker-next, ' +
            '.p-datepicker-header .p-datepicker-month, ' +
            '.p-datepicker-header .p-datepicker-year'
        );

        // Add new event handlers
        navElements.forEach((navElement: HTMLElement) => {
            const handler = (event: Event) => {
                event.stopPropagation();
                this.blockOverlayDismiss.emit();
                
                // Optional: Re-enable dismissal after a short delay
                setTimeout(() => {
                    this.unblockOverlayDismiss.emit();
                }, 300);
            };
            
            navElement.addEventListener('click', handler);
            
            // Keep track of the handler so we can remove it later
            this.navElementClickHandlers.push({
                element: navElement,
                handler: handler
            });
        });
    }

    private removeExistingEventHandlers() {
        // Remove all previously attached event handlers
        this.navElementClickHandlers.forEach(({element, handler}) => {
            element.removeEventListener('click', handler);
        });
        
        // Clear the array
        this.navElementClickHandlers = [];
    }

    /**
     * Checks, if the left time form control is required. The left time form control is required, if the showTime property is set to true.
     * @returns 
     */
    isLeftTimeFormControlRequired(): boolean {
        return this.showTime;
    }

    /**
     * Checks, if the right time form control is required. The right time form control is required, if the showTime property is set to true and the selection mode is set to range.
     * @returns 
     */
    isRightTimeFormControlRequired(): boolean {
        return this.showTime && this.selectionMode === 'range';
    }

    getLeftTimeFormControlLabel(): string {
        let dateValue;
        if(this.selectionMode === 'range') {
            dateValue = this.calendarFormControl?.value?.[0];
        } else {
            dateValue = this.calendarFormControl?.value;
        }
        if(dateValue) {
            // format to dd.MM.
            return "Uhrzeit am " + this.datePipe.transform(dateValue, 'dd.MM.') + ":";
        }
        return "Uhrzeit:"
    }

    getRightTimeFormControlLabel(): string {
        let dateValue = this.calendarFormControl?.value?.[1];
        if(dateValue) {
            // format to dd.MM.
            return "Uhrzeit am " + this.datePipe.transform(dateValue, 'dd.MM.') + ":";
        }
        return "Uhrzeit:"
    }

    ngOnDestroy(): void {
        this.calendarFormGroupSubscription?.unsubscribe();

        this.removeExistingEventHandlers();
        
        // Disconnect the mutation observer
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
            this.mutationObserver = null;
        }
    }

    onInputChange(event: any): void {
        let valid = false;
        let timeAdded = false;
        let value: NxEditCalendarInputValue | undefined = undefined;
        let actualSelectionMode = this.calendarFormGroup.get('calendar')?.value?.[0] ? 'range' : 'single';
        // check validity of the form
        if(this.calendarFormControl.valid) {
            if(actualSelectionMode === 'single') {
                let date = this.calendarFormGroup.get('calendar')?.value;
                if (date && date instanceof Date) {
                    valid = true;
                }
            }
            if(actualSelectionMode === 'range') {
                let date0 = this.calendarFormGroup.get('calendar')?.value[0];
                let date1 = this.calendarFormGroup.get('calendar')?.value[1];
                if (date0 && date0 instanceof Date && date1 && date1 instanceof Date) {
                    valid = true;
                }
            }
        }
        if (valid) {
            // check, if we have to add times to the date
            if(actualSelectionMode === 'single') {
                let date = this.calendarFormGroup.get('calendar')?.value;
                let time = this.calendarFormGroup.get('leftTimeFormControl')?.value;
                if(this.validateTimeString(time)) {
                    date = this.addTimeToDate(date, time);
                    timeAdded = true;
                }
                value = {
                    precision: timeAdded ? 'DATE_TIME' : 'DATE',
                    dateTime: date.toISOString(),
                };
            }
            if(actualSelectionMode === 'range') {
                let date0 = this.calendarFormGroup.get('calendar')?.value[0];
                let date1 = this.calendarFormGroup.get('calendar')?.value[1];
                let time0 = this.calendarFormGroup.get('leftTimeFormControl')?.value;
                let time1 = this.calendarFormGroup.get('rightTimeFormControl')?.value;
                if(this.validateTimeString(time0)) {
                    date0 = this.addTimeToDate(date0, time0);
                    timeAdded = true;
                }
                if(this.validateTimeString(time1)) {
                    date1 = this.addTimeToDate(date1, time1);
                    timeAdded = true;
                }
                if(date0 && date1 && date0 < date1) {
                    value = {
                        precision: timeAdded ? 'DATE_TIME' : 'DATE',
                        fromDateTime: date0.toISOString(),
                        toDateTime: date1.toISOString(),
                    }
                    this.showTimesError = false;
                } else {
                    this.showTimesError = true;
                }
            }
            
        }
        if(this.onChange) {
            this.onChange(value);
        }
    }

    onTimeBlur(event: any, formControlName: string) {
        let value = this.calendarFormGroup.get(formControlName)?.value;
        if (!value) return;
        
        // Handle numeric input like "1300"
        if (/^\d+$/.test(value)) {
            const timeString = value.padStart(4, '0');
            const hours = timeString.substring(0, 2);
            const minutes = timeString.substring(2, 4);
            
            // Validate hours and minutes
            if (parseInt(hours) < 24 && parseInt(minutes) < 60) {
            value = `${hours}:${minutes}`;
            }
            this.calendarFormGroup.get(formControlName)?.setValue(value);
        }
    }

    onOverlayOpen($event: any) {
        this.blockOverlayDismiss.emit();
    }

    onOverlayClose($event: any) {
        this.unblockOverlayDismiss.emit();
    }

    writeValue(nxEditCalendarInputValue: NxEditCalendarInputValue): void {
        if(nxEditCalendarInputValue){
            this.blockNextChangeCycle = true;
            if(nxEditCalendarInputValue.precision === 'DATE_TIME') {
                if(nxEditCalendarInputValue.fromDateTime && nxEditCalendarInputValue.toDateTime) {
                    this.calendarFormControl.setValue([new Date(nxEditCalendarInputValue.fromDateTime), new Date(nxEditCalendarInputValue.toDateTime)], {emitEvent: false});
                    this.leftTimeFormControl.setValue(this.datePipe.transform(new Date(nxEditCalendarInputValue.fromDateTime), 'HH:mm'), {emitEvent: false});
                    this.rightTimeFormControl.setValue(this.datePipe.transform(new Date(nxEditCalendarInputValue.toDateTime), 'HH:mm'), {emitEvent: false});
                    this.calendarFormGroup.updateValueAndValidity();
                } else if(nxEditCalendarInputValue.dateTime) {
                    this.calendarFormControl.setValue(new Date(nxEditCalendarInputValue.dateTime), {emitEvent: false});
                    this.leftTimeFormControl.setValue(this.datePipe.transform(new Date(nxEditCalendarInputValue.dateTime), 'HH:mm'), {emitEvent: false});
                    this.calendarFormGroup.updateValueAndValidity();
                }
            } else if(nxEditCalendarInputValue.precision === 'DATE') {
                if(nxEditCalendarInputValue.dateTime) {
                    this.calendarFormControl.setValue(new Date(nxEditCalendarInputValue.dateTime));
                }
            }
        } else {
            this.calendarFormGroup.reset();
        }
    }

    registerOnChange(fn: (value: any) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    isRequired(): boolean {
        return this.ngControl?.control?.hasValidator(Validators.required);
    }

    generateTimeOptions(): string[] {
        const options: string[] = [];
        for (let hour = 0; hour < 24; hour++) {
          for (let minute = 0; minute < 60; minute += 30) {
            const hourStr = hour.toString().padStart(2, '0');
            const minuteStr = minute.toString().padStart(2, '0');
            const timeStr = `${hourStr}:${minuteStr}`;
            options.push(timeStr);
          }
        }
        return options;
    }

    addTimeToDate(date: Date, time: any): Date {
        if(!time) {
            return date;
        }
        const [hours, minutes] = time?.split(':').map(Number);
        const newDate = new Date(date);
        newDate.setHours(hours, minutes);
        return newDate;
    }

    onTimeFocus(event: any) {
        event.target.select();
    }

    validateTimeString(time: string): boolean {
        const timePattern = /^([01]\d|2[0-3]):([0-5]\d)$/;
        return timePattern.test(time);
    }

}